import React from 'react';
import { Form, Button, notification, Select, DatePicker, Row, Col, Popconfirm } from 'antd';
import axiosInstance from "utilities/axios";
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';

const EditRMForm = ({ region, onChange }) => {
    const [form] = Form.useForm();

    const [selectedRM, setSelectedRM] = useState(null);
    const [employeeOptions, setEmployeeOptions] = useState([]);
    const [submitEnabled, setSubmitEnabled] = useState(true);

    const [showConfirm, setShowConfirm] = useState(false);

    useEffect(() => {
        axiosInstance
          .get("/employees")
          .then((response) => {
            setEmployeeOptions(response.data);
          });
    }, []);

    const onFinish = (values) => {
        axiosInstance
          .put(`/regions/${region.regionCode}/rms/${values.employee}`, {
            title: values.title,
            startDate: values.startDate.format('YYYY-MM-DD'),
            endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : null,
            id: selectedRM.id
          })
          .then(() => {
            notification.success({
              message: 'Regional Manager Updated',
              description: 'The Regional Manager has been successfully updated.'
            });
            onChange();
          })
          .catch((error) => {
            notification.error({
              message: 'Failed to update Regional Manager',
              description: error.response.data.error
            });
          })
    };

    return (
        <Form 
            form={form}
            layout='vertical'
            onFinish={onFinish}
            onValuesChange={(changedValues, allValues) => {
                const rm = region.rms.find((rm) => rm.initials === changedValues.employee);

                if (changedValues.employee) {
                    form.setFieldsValue({
                        title: rm?.title,
                        startDate: rm?.startDate ? dayjs(rm.startDate) : null,
                        endDate: rm?.endDate ? dayjs(rm.endDate) : null
                    });
                };

                if ((changedValues.title && changedValues.title !== selectedRM.title) ||
                    (changedValues.startDate && changedValues.startDate.format('YYYY-MM-DD') !== selectedRM.startDate) ||
                    (changedValues.endDate && changedValues.endDate.format('YYYY-MM-DD') !== selectedRM.endDate) ||
                    (!changedValues.endDate && selectedRM && selectedRM.endDate !== null)) 
                {
                    setSubmitEnabled(false);
                } else {
                    setSubmitEnabled(true);
                }

                if (changedValues.title && changedValues.title === 'Regional Manager' && selectedRM.title !== 'Regional Manager') {
                    setShowConfirm(true);
                } else {
                    setShowConfirm(false);
                }
            }}
        >
            <Form.Item 
                name='employee'
                label="Select Regional Manager"
            >
                <Select
                    name='rm'
                    placeholder="Select an RM"
                    style={{ width: '100%' }}
                    onChange={(value) => {setSelectedRM(region.rms.find((rm) => rm.initials === value))}}
                    filterOption={(input, option) => {
                        const optionData = employeeOptions.find(
                            (opt) => opt.initials === option.value
                        );
                        if (!optionData) {
                            return false;
                        }
                        return (
                            optionData.firstName.toLowerCase().includes(input.toLowerCase()) ||
                            optionData.lastName.toLowerCase().includes(input.toLowerCase()) ||
                            optionData.initials.toLowerCase().includes(input.toLowerCase())
                        );
                    }}
                >
                    {region.rms.filter((rm) => rm.title !== 'Regional Manager').map((rm) => (
                        <Select.Option
                            key={rm.initials}
                            value={rm.initials}
                        >
                            {rm.firstName} {rm.lastName} - {rm.initials}
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>
            <Form.Item
                name='title'
                label="Regional Manager Type"
            >
                <Select 
                    placeholder="Select a title"
                    style={{ width: '100%' }}
                    disabled={!selectedRM}
                >
                    <Select.Option value="Regional Manager">Regional Manager</Select.Option>
                    <Select.Option value="Assistant Regional Manager">Assistant Regional Manager</Select.Option>
                    <Select.Option value="Temporary Regional Manager">Temporary Regional Manager</Select.Option>
                </Select>
            </Form.Item>
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item
                        name='startDate'
                        label="Start Date"
                    >
                        <DatePicker
                            style={{ width: '100%' }}
                            disabled={!selectedRM}
                        />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        name='endDate'
                        label="End Date"
                    >
                        <DatePicker
                            style={{ width: '100%' }}
                            disabled={!selectedRM}
                        />
                    </Form.Item>
                </Col>
            </Row>
            
            {showConfirm ? (
                <Popconfirm
                    title={
                        <>
                            Are you sure you want to go through with this change?<br/>
                            This will remove the current Regional Manager.
                        </>
                    }

                    onConfirm={() => {
                        setShowConfirm(false);
                        setSubmitEnabled(false);
                        onFinish(form.getFieldsValue());
                    }}
                    okText="Yes"
                    cancelText="No"
                    placement="bottom"
                >
                    <Button
                        type="primary"
                        style={{ width: '100%' }}
                        disabled={submitEnabled}
                    >
                        Update Regional Manager
                    </Button>
                </Popconfirm>
            ) : (
                <Button
                    type="primary"
                    htmlType="submit"
                    style={{ width: '100%' }}
                    disabled={submitEnabled}
                >
                    Update Regional Manager
                </Button>
            )}
        </Form>
    );
};

export default EditRMForm;